import { useCallback } from "react";
import {
  focusNextTabCell,
  focusPrevTabCell,
  focusEnterTarget,
} from "./tabNavigation";

interface UseCellNavigationOptions {
  /**
   * Called before the focus moves on Tab/Enter.
   * Use this to flush pending edits (e.g. call handleBlur or target.blur()).
   * Receives the keyboard event target element.
   */
  onBeforeNavigate?: (target: HTMLInputElement) => void;
}

/**
 * Returns a keyDown handler implementing the shared Tab / Enter navigation
 * pattern used by PriceCell, QuantityCell, and WeightCell.
 *
 * - Tab / Shift+Tab: move to next/prev tab cell
 * - Enter: move down to the same column in the next row (spreadsheet-style)
 * - Shift+Enter: jump to the new-row SKU input (the table's last line)
 */
export function useCellNavigation(options?: UseCellNavigationOptions) {
  const onBeforeNavigate = options?.onBeforeNavigate;

  return useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Tab") {
        const target = e.currentTarget;
        onBeforeNavigate?.(target);
        const moved = e.shiftKey
          ? focusPrevTabCell(target)
          : focusNextTabCell(target);
        if (moved) {
          e.preventDefault();
        }
      } else if (e.key === "Enter") {
        e.preventDefault();
        onBeforeNavigate?.(e.currentTarget);
        focusEnterTarget(e.currentTarget, e.shiftKey);
      }
    },
    [onBeforeNavigate],
  );
}
